import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState, InactiveNotice } from "@/components/dash/Common";
import FileUpload from "@/components/dash/FileUpload";
import { Pill } from "@/components/Badges";
import { FullLoader } from "@/components/ProtectedRoute";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, openFile } from "@/lib/api";
import { fmtDateTime, isPackageActive } from "@/lib/format";

function SubmitDialog({ assignment, onClose, onDone }) {
  const sub = assignment.submission || {};
  const [form, setForm] = useState({ text: sub.text || "", file_id: sub.file_id || "", file_name: sub.file_name || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/student/assignments/${assignment.id}/submit`, form);
      toast.success("Tugas terkumpul");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assignment-submit-dialog">
        <DialogHeader><DialogTitle>{assignment.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 whitespace-pre-line">{assignment.description}</p>
        <Textarea rows={5} placeholder="Tulis jawaban Anda..." value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="assignment-text-input" />
        <FileUpload fileName={form.file_name} onUploaded={(r) => setForm({ ...form, ...r })} testid="assignment-file-input" />
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-slate-900" data-testid="assignment-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kumpulkan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentCard({ a, onOpen }) {
  const s = a.submission;
  const late = a.due_at && new Date(a.due_at) < new Date();
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 flex flex-wrap justify-between gap-4" data-testid={`assignment-card-${a.id}`}>
      <div className="max-w-2xl">
        <div className="flex gap-2 items-center flex-wrap">
          <p className="font-display text-lg font-semibold text-slate-900">{a.title}</p>
          {s ? (s.score != null ? <Pill tone="green">Dinilai: {s.score}/{a.max_score}</Pill> : <Pill tone="amber">Terkumpul</Pill>) : <Pill tone={late ? "red" : "slate"}>{late ? "Lewat tenggat" : "Belum dikumpulkan"}</Pill>}
        </div>
        <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{a.description}</p>
        {a.due_at && <p className="text-xs text-slate-500 mt-3 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Tenggat {fmtDateTime(a.due_at)}</p>}
        {s?.feedback && <p className="mt-3 text-sm rounded bg-slate-50 p-3">Umpan balik: {s.feedback}</p>}
        {s?.file_id && <button className="mt-2 text-xs text-amber-600 underline" onClick={() => openFile(s.file_id).catch((e) => toast.error(errMsg(e)))}>Lihat file: {s.file_name}</button>}
      </div>
      {(!s || s.score == null) && <Button variant="outline" onClick={() => onOpen(a)} data-testid={`open-assignment-${a.id}`}>{s ? "Perbarui" : "Kerjakan"}</Button>}
    </div>
  );
}

export default function StudentAssignments() {
  const { user } = useAuth();
  const { data, loading, reload } = useApi("/student/assignments");
  const [active, setActive] = useState(null);
  if (loading) return <FullLoader />;
  return (
    <div data-testid="student-assignments-page">
      <PageHeader eyebrow="Tugas" title="Tugas & penilaian" description="Kumpulkan jawaban berupa teks atau file. Nilai dan umpan balik dari pengajar akan tampil di sini." />
      {!isPackageActive(user) ? <InactiveNotice /> : (
        <div className="space-y-4">{data?.length ? data.map((a) => <AssignmentCard key={a.id} a={a} onOpen={setActive} />) : <EmptyState text="Belum ada tugas." />}</div>
      )}
      {active && <SubmitDialog assignment={active} onClose={() => setActive(null)} onDone={() => { setActive(null); reload(); }} />}
    </div>
  );
}
